import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, EngineKeyline, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { createContent, deleteContent, listContent, type RecentContentItem, type RecentContentList } from '@/data/sources-social';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { CHANNELS, CHANNEL_ASPECTS, QUICK_STARTS, channelLabel, defaultAspect, statusLabel } from '@/data/studio-layouts';
import type { AspectKey, Channel, ContentItem, ContentPage, Format } from '@/types/social';
import { BrandKitPanel } from './BrandKitPanel';
import { BlogLibrarySection } from './BlogLibrarySection';
import { VoiceCopilot, type VoiceProviderConfig } from '@/voice';
import { getVoiceStatus } from '@/voice/net/voiceApi';
import { useContentLibraryFlag } from '@/hooks/useContentLibraryFlag';
import { SeoAutopilotPanel } from './SeoAutopilotPanel';
import { StudioRecent } from './library/StudioRecent';

/* Studio home: serif hero + content-type quick-starts + recent content rail.
 * The single entry point into the AI Content Studio v0 workflow. */

function createErrorMessage(err: unknown): string {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return 'Your session has expired or could not be verified. Refresh the page and try again.';
  }
  return 'Could not create content. Try again.';
}

function deleteErrorMessage(err: unknown): string {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return 'Your session has expired or you are not permitted to delete this draft. Refresh and try again.';
  }
  return 'Could not delete this draft. Try again.';
}

function seedPages(format: Format): ContentPage[] {
  const mk = (idx: number, layoutKey: string): ContentPage => ({ idx, layoutKey, slots: { logo: true }, align: 'start', textSize: 'm' });
  return format === 'carousel'
    ? [mk(0, 'carousel-cover'), mk(1, 'carousel-tip'), mk(2, 'carousel-cta')]
    : [mk(0, 'hero-media')];
}

type DialogState = { channel: Channel; format: Format; aspect: AspectKey; title: string };

export function StudioHome() {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ContentItem | null>(null);
  const queryClient = useQueryClient();
  const [voiceOpen, setVoiceOpen] = useState(false);
  // content_library_v1 (workspace flag, default OFF). OFF / loading / error ->
  // this page renders exactly as before. ON -> the long lists move to the Library
  // ("Recent 6 + View all"), the duplicate in-card nav links go (the sidebar has them).
  const libraryFlag = useContentLibraryFlag();
  const libraryOn = libraryFlag.enabled;

  // Domain isolation (rules 1,6): scope Recent Content to the active 'Viewing'
  // domain. projectId in the queryKey makes switching Viewing refetch + drop the
  // stale cross-domain list; null (no domain / "All domains" lens) shows the
  // honest "Across your workspace" view where unproven items may appear.
  const { active, isAllDomains, select } = useActiveDomain();
  const projectId = active?.projectId ?? null;

  // Owner rules 1-2: Recent Content scope toggle. DEFAULT = the selected Viewing
  // domain (strict isolation preserved); "All domains" is an explicit, LOCAL opt-in
  // that widens ONLY this list — Blog & articles and every other surface stay on
  // the global Viewing domain.
  const [recentScope, setRecentScope] = useState<'domain' | 'all'>('domain');
  const listProjectId = recentScope === 'all' ? null : projectId;
  const recentKey = ['social-content', recentScope === 'all' ? 'all' : projectId] as const;

  const recent = useQuery<RecentContentList>({
    queryKey: recentKey,
    queryFn: () => listContent(null, listProjectId),
    enabled: !libraryFlag.isLoading && !libraryOn,
  });

  // Aura voice capability probe. Fail-closed: any non-true / error / loading
  // state leaves the launcher disabled with an honest 'Voice unavailable'
  // tooltip and never attempts a live call.
  const voiceStatus = useQuery({
    queryKey: ['voice-status'],
    queryFn: () => getVoiceStatus(),
    retry: false,
    staleTime: 60_000,
  });
  const voiceAvailable = voiceStatus.data?.voice_available === true;
  const voiceConfig: VoiceProviderConfig = {
    stt: voiceStatus.data?.stt_provider ?? 'mock',
    tts: voiceStatus.data?.tts_provider ?? 'mock',
    ttsVoiceId: voiceStatus.data?.tts_voice_id ?? 'aura-2-pandora-en',
  };
  const voiceTip = voiceAvailable
    ? 'Talk to Aura'
    : 'Voice unavailable' + (voiceStatus.data?.reason ? ' — ' + voiceStatus.data.reason : '');

  // Optimistic + honest: drop the row locally, roll back on failure, then
  // invalidate so the list reconciles with the server's soft-deleted truth.
  const del = useMutation({
    mutationFn: (id: number) => deleteContent(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: recentKey });
      const prev = queryClient.getQueryData<RecentContentList>(recentKey);
      queryClient.setQueryData<RecentContentList>(recentKey, (old) =>
        old ? { ...old, items: old.items.filter((c) => c.id !== id) } : old,
      );
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(recentKey, ctx.prev);
    },
    onSuccess: () => setPendingDelete(null),
    // Broad invalidate so a soft-deleted item disappears from EVERY scope (domain,
    // All, Unassigned) + the editor — the tombstone is excluded by all list reads.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['social-content'] }),
  });

  const create = useMutation({
    mutationFn: (d: DialogState) =>
      createContent({
        channel: d.channel, contentType: 'post', title: d.title, layoutKey: seedPages(d.format)[0].layoutKey,
        // Rule 5: a new draft inherits the active 'Viewing' domain (server re-
        // validated); with no domain selected it stays Unassigned.
        projectId,
        visualBrief: { brief: '', stylePreset: '', aspect: d.aspect, imagePrompt: '', format: d.format, pages: seedPages(d.format) },
      }),
    onSuccess: (item) => navigate(`/social/content-studio/${item.id}`),
  });

  const openDialog = (channel: Channel, format: Format) =>
    setDialog({ channel, format, aspect: defaultAspect(channel), title: '' });

  // Rule 4: opening a draft RESTORES its ORIGINAL domain context. Pin the shell's
  // Viewing domain to the ROW's own proven project_id (not whatever was selected
  // while discovering it in the All view) so the editor + its media + any
  // recommendations operate under the draft's OWN domain — no cross-domain bleed.
  // Unassigned drafts carry no domain to restore; they open workspace-wide.
  const openItem = (item: RecentContentItem) => {
    const pid = item.domain.projectId;
    if (pid && pid > 0 && pid !== projectId) {
      void select(pid);
    }
    navigate('/social/content-studio/' + item.id);
  };

  // A1 (UI IVG): hold a skeleton until the flag resolves, so neither layout flashes.
  if (libraryFlag.isLoading) {
    return <div className="mx-auto max-w-5xl"><LoadingState rows={5} /></div>;
  }

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="AI Content Studio"
        subtitle="Plan a post, generate copy and a visual brief, choose stock media and a layout, preview per channel, and route for approval."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ai"
              onClick={() => setVoiceOpen(true)}
              disabled={!voiceAvailable}
              title={voiceTip}
              aria-label={voiceAvailable ? 'Talk to Aura' : 'Voice unavailable'}
            >
              {voiceAvailable ? 'Hey Aura' : 'Voice unavailable'}
            </Button>
            <Button variant="secondary" onClick={() => setBrandOpen(true)}>Brand Kit</Button>
            <Badge tone="ai">v0 · Beta</Badge>
          </div>
        }
      />
      <EngineKeyline className="mb-6" />

      <Card className="mb-8 p-6">
        <h2 className="font-display text-xl font-semibold text-text-primary">What will you create today?</h2>
        <p className="mt-1 text-sm text-text-secondary">Pick a format to start. Everything is advisory and human-approved — nothing publishes externally.</p>
        <button
          onClick={() => navigate('/social/content-studio/prompt')}
          className="mt-4 flex w-full items-center justify-between gap-3 rounded-lg border border-ai/40 bg-ai/5 px-4 py-3 text-left transition-colors hover:border-ai hover:bg-ai/10"
        >
          <span>
            <span className="block text-sm font-semibold text-text-primary">Create from prompt</span>
            <span className="mt-0.5 block text-xs text-text-secondary">Describe a campaign in one line — generate approval-ready copy, hooks, CTAs and a visual brief per channel.</span>
          </span>
          <Badge tone="ai">AI</Badge>
        </button>
        <div className="mt-4 flex flex-wrap gap-2">
          {QUICK_STARTS.map((qs) => (
            <button
              key={qs.key}
              onClick={() => openDialog(qs.channel, qs.format)}
              className="rounded-lg border border-border-default bg-surface px-4 py-3 text-sm font-medium text-text-primary transition-colors hover:border-ai hover:bg-ai/5"
            >
              {qs.label}
            </button>
          ))}
        </div>
        {!libraryOn && <div className="mt-4 flex flex-wrap gap-3 text-xs text-text-secondary">
          <a className="underline hover:text-text-primary" href="/social/templates">Templates</a>
          <a className="underline hover:text-text-primary" href="/social/calendar">Calendar</a>
          <a className="underline hover:text-text-primary" href="/social/connections">Connections</a>
          <a className="underline hover:text-text-primary" href="/social/content-studio/stock">Stock media</a>
        </div>}
      </Card>

      {libraryOn ? (
        <>
          <SeoAutopilotPanel />
          <StudioRecent />
        </>
      ) : (<>
      <section className="space-y-3">
        {/* 'Social Media' category header — styled IDENTICALLY to the 'Blog &
            articles' label on BlogLibrarySection (font-display text-lg font-semibold
            text-text-primary), so Recent Content and the Blog library read as
            matching category headers. Label only: the list below still shows the
            SAME social data (bc_social_content_items), unchanged. */}
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-text-primary">Social Media</h2>
        </div>
        <p className="text-xs text-text-secondary">
          Recent content — draft posts for your social channels.
        </p>
        {/* Owner rules 1-3: explicit LOCAL scope toggle for Recent Content ONLY.
            Default = the selected Viewing domain (strict isolation preserved); the
            "All domains" lens is an opt-in that shows EVERY workspace item, each
            labelled with its originating domain (+ Unassigned). Blog & articles and
            all other surfaces are unaffected. */}
        <div className="flex flex-wrap items-center gap-2">
          <div role="tablist" aria-label="Recent content scope" className="inline-flex rounded-md border border-border-default p-0.5">
            <button
              role="tab"
              aria-selected={recentScope === 'domain'}
              onClick={() => setRecentScope('domain')}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                recentScope === 'domain' ? 'bg-ai/10 text-ai' : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {projectId ? (active?.hostname ?? 'This domain') : 'This domain'}
            </button>
            <button
              role="tab"
              aria-selected={recentScope === 'all'}
              onClick={() => setRecentScope('all')}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                recentScope === 'all' ? 'bg-ai/10 text-ai' : 'text-text-secondary hover:text-text-primary',
              )}
            >
              All domains
            </button>
          </div>
        </div>
        <p className="text-[11px] text-text-tertiary">
          {recentScope === 'all'
            ? 'Across your workspace — every domain, each card labelled with its domain, including drafts not yet assigned to one.'
            : projectId
              ? `Showing content for ${active?.hostname ?? 'the selected domain'} only. Switch to All domains to find drafts saved under another domain.`
              : isAllDomains
                ? 'Across your workspace — including drafts not yet assigned to a domain.'
                : 'Across your workspace.'}
        </p>
        {recent.isLoading ? (
          <LoadingState rows={3} />
        ) : recent.isError ? (
          <ErrorState error={recent.error} onRetry={() => void recent.refetch()} />
        ) : (recent.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No content here"
            teach={
              recentScope === 'domain' && projectId
                ? 'No drafts under this domain yet. A draft you saved may live under another domain — switch to All domains above to find it. Or start from a format above.'
                : 'Start from a format above. Drafts you create appear here with their honest review and publish status.'
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {recent.data!.items.map((item) => (
              <RecentCard
                key={item.id}
                item={item}
                showDomain={recentScope === 'all'}
                onOpen={() => openItem(item)}
                onDelete={() => setPendingDelete(item)}
              />
            ))}
          </div>
        )}
      </section>

      <div className="mt-8">
        <BlogLibrarySection />
      </div>
      </>)}

      {dialog && (
        <NewContentDialog
          value={dialog}
          onChange={setDialog}
          onCancel={() => setDialog(null)}
          onCreate={() => create.mutate(dialog)}
          pending={create.isPending}
          error={create.isError ? createErrorMessage(create.error) : null}
        />
      )}
      {pendingDelete && (
        <ConfirmDeleteDialog
          item={pendingDelete}
          onCancel={() => { if (!del.isPending) setPendingDelete(null); }}
          onConfirm={() => del.mutate(pendingDelete.id)}
          pending={del.isPending}
          error={del.isError ? deleteErrorMessage(del.error) : null}
        />
      )}
      {voiceOpen && voiceAvailable && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
          <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={() => setVoiceOpen(false)} aria-hidden />
          <div role="dialog" aria-modal="true" aria-label="Aura voice assistant" className="relative z-10 mt-16 w-full max-w-md sm:mt-0">
            <div className="mb-2 flex justify-end">
              <Button variant="secondary" onClick={() => setVoiceOpen(false)} aria-label="Close Aura">Close</Button>
            </div>
            <VoiceCopilot config={voiceConfig} />
          </div>
        </div>
      )}
      <BrandKitPanel open={brandOpen} onClose={() => setBrandOpen(false)} />
    </div>
  );
}

function RecentCard({ item, showDomain, onOpen, onDelete }: { item: RecentContentItem; showDomain: boolean; onOpen: () => void; onDelete: () => void }) {
  const st = statusLabel(item.reviewStatus, item.publishState, item.scheduledFor, item.providerRef);
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface p-4 transition-colors hover:border-ai">
      <div className="flex items-start justify-between gap-2">
        {/* Clickable title -> reopen the saved draft in the editor (never a dead-end). */}
        <button onClick={onOpen} className="min-w-0 flex-1 truncate text-left text-sm font-medium text-text-primary hover:text-ai">
          {item.title || 'Untitled draft'}
        </button>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      <button onClick={onOpen} className="text-left">
        <p className="line-clamp-2 text-xs text-text-secondary">{item.bodyText || item.visualBrief?.pages[0]?.slots.headline || 'No copy yet'}</p>
      </button>
      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-[11px] text-text-tertiary">
        {/* Rule 3: in the All-domains view every card is LABELLED with its OWN
            originating domain (or "Unassigned") — never misattributed. */}
        {showDomain && (
          <Badge tone={item.domain.unassigned ? 'neutral' : 'ai'}>
            {item.domain.unassigned ? 'Unassigned' : (item.domain.label || item.domain.host || 'Domain')}
          </Badge>
        )}
        <span>{channelLabel(item.channel)}</span>
        <span>·</span>
        <span>{item.visualBrief?.format === 'carousel' ? `Carousel · ${item.visualBrief.pages.length}p` : 'Single'}</span>
        {/* internal AI provenance is kept in the audit record, not shown to customers */}
      </div>
      {/* Rule 5 + owner add-on: a saved draft is NEVER an inaccessible card. "Open"
          reopens it in the editor (Concept -> Copy -> Media -> Layout -> Preview +
          approve/reject) with its OWN domain context restored (rule 4); "Delete" is
          at PARITY with Blogs (soft, reversible, workspace-scoped) and present on
          every card in BOTH views, regardless of review status. */}
      <div className="mt-1 flex items-center justify-end gap-2 border-t border-border-subtle pt-2">
        <Button variant="secondary" onClick={onOpen} aria-label={item.title ? `Open ${item.title}` : 'Open draft'}>Open</Button>
        <button
          onClick={onDelete}
          className="shrink-0 rounded px-2 py-1 text-xs font-medium text-text-tertiary transition-colors hover:text-danger focus:text-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
          aria-label={item.title ? `Delete: ${item.title}` : 'Delete draft'}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function ConfirmDeleteDialog({
  item, onCancel, onConfirm, pending, error,
}: {
  item: ContentItem;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
  error: string | null;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={() => !pending && onCancel()} aria-hidden />
      <div role="alertdialog" aria-modal="true" aria-label="Delete draft" className="relative z-10 mt-16 w-full max-w-sm rounded-xl border border-border-default bg-surface shadow-lg sm:mt-0">
        <header className="border-b border-border-subtle px-5 py-3">
          <h2 className="text-sm font-semibold text-text-primary">Delete this draft?</h2>
        </header>
        <div className="space-y-3 px-5 py-4">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{item.title || 'Untitled draft'}</span> will be removed from your Studio. This is a soft delete (reversible) and is recorded for audit.
          </p>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onCancel} disabled={pending}>Cancel</Button>
            <button
              onClick={onConfirm}
              disabled={pending}
              className="inline-flex h-control items-center justify-center rounded-md bg-danger px-4 text-sm font-medium text-white transition-colors hover:bg-danger/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 disabled:opacity-60"
            >
              {pending ? 'Deleting…' : 'Delete draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewContentDialog({
  value, onChange, onCancel, onCreate, pending, error,
}: {
  value: DialogState;
  onChange: (v: DialogState) => void;
  onCancel: () => void;
  onCreate: () => void;
  pending: boolean;
  error: string | null;
}) {
  const aspects = CHANNEL_ASPECTS[value.channel] ?? CHANNEL_ASPECTS.generic;
  const sel = 'h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={() => !pending && onCancel()} aria-hidden />
      <div role="dialog" aria-modal="true" aria-label="New content" className="relative z-10 mt-16 w-full max-w-md rounded-xl border border-border-default bg-surface shadow-lg sm:mt-0">
        <header className="border-b border-border-subtle px-5 py-3">
          <h2 className="text-sm font-semibold text-text-primary">New content</h2>
          <p className="mt-0.5 text-xs text-text-secondary">Choose the channel, format and orientation.</p>
        </header>
        <div className="space-y-3 px-5 py-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Channel</span>
            <select className={sel} value={value.channel} onChange={(e) => { const channel = e.target.value as Channel; onChange({ ...value, channel, aspect: (CHANNEL_ASPECTS[channel] ?? CHANNEL_ASPECTS.generic)[0].key }); }}>
              {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Format</span>
              <select className={sel} value={value.format} onChange={(e) => onChange({ ...value, format: e.target.value as Format })}>
                <option value="single">Single</option>
                <option value="carousel">Carousel</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Orientation</span>
              <select className={cn(sel)} value={value.aspect} onChange={(e) => onChange({ ...value, aspect: e.target.value as AspectKey })}>
                {aspects.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Working title <span className="text-text-tertiary">(optional)</span></span>
            <input className={sel} value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder="Summer launch teaser" />
          </label>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onCancel} disabled={pending}>Cancel</Button>
            <Button variant="ai" onClick={onCreate} disabled={pending}>{pending ? 'Creating…' : 'Create draft'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
